import { AdminRole } from "@repo/api-dtos"
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router"
import { ArrowLeft } from "lucide-react"
import { useCallback } from "react"
import { BuyerPromotionForm } from "@/components/buyer-promotions/buyer-promotion-form"
import {
	type BuyerPromotionFormValues,
	toUpdateBuyerPromotionInput,
} from "@/components/buyer-promotions/buyer-promotion-form.helpers"
import { Button } from "@/components/ui/button"
import { Skeleton } from "@/components/ui/skeleton"
import { useAdminRoles } from "@/hooks/use-admin-roles"
import {
	useBuyerPromotion,
	useUpdateBuyerPromotion,
} from "@/queries/marketplace"

export const Route = createFileRoute("/buyer-promotions/$promotionId/edit")({
	component: EditBuyerPromotionPage,
})

function EditBuyerPromotionPage() {
	const { promotionId } = Route.useParams()
	const navigate = useNavigate()
	const { promotion, isLoading, error } = useBuyerPromotion(promotionId)
	const updateMutation = useUpdateBuyerPromotion()
	const { hasRole, isLoading: rolesLoading } = useAdminRoles()

	const handleSubmit = useCallback(
		async (values: BuyerPromotionFormValues) => {
			if (!promotion) {
				return
			}

			await updateMutation.mutateAsync(
				toUpdateBuyerPromotionInput(values, promotion),
			)
			navigate({
				to: "/buyer-promotions/$promotionId",
				params: { promotionId },
			})
		},
		[promotion, promotionId, updateMutation, navigate],
	)

	if (isLoading || rolesLoading) {
		return (
			<div className="flex flex-col gap-6 p-6">
				<Skeleton className="h-8 w-64" />
				<div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
					<Skeleton className="h-64" />
					<Skeleton className="h-64" />
				</div>
			</div>
		)
	}

	if (error || !promotion) {
		return (
			<div className="flex flex-col items-start gap-3 p-6">
				<p className="text-bad">
					{error?.message || "The buyer promotion could not be found."}
				</p>
				<Button variant="outline" asChild>
					<Link to="/buyer-promotions">
						<ArrowLeft />
						Back to buyer promotions
					</Link>
				</Button>
			</div>
		)
	}

	if (!hasRole(AdminRole.MarketplaceCoupons)) {
		return (
			<div className="p-6">
				<p className="text-muted-foreground">
					Only admins with the coupons role can change buyer promotions.
				</p>
			</div>
		)
	}

	return (
		<BuyerPromotionForm
			mode="edit"
			promotion={promotion}
			onSubmit={handleSubmit}
			isSubmitting={updateMutation.isPending}
		/>
	)
}
